import { useEffect, useRef, useState } from 'react';
import type { ParsedSpec } from '../solidworks-parser';
import type { SheetPart2D, SheetStock2D } from '@app/linecut-core';
import { uid } from '@app/linecut-core';
import { useCalculatorWorker } from '../../worker/useCalculatorWorker';
import { Collapsible } from '../../components/Collapsible';
import { parseSheetMaterial } from './parseSheetMaterial';
import { SheetCalcResultView, type SheetGroupResult } from './SheetCalcResultView';

interface Props {
  spec: ParsedSpec;
  onResultsChange?: (results: SheetGroupResult[]) => void;
}

// ─── Stock row (UI model) ─────────────────────────────────────────────────────

interface SheetStockRow {
  id: string;
  groupKey: string;
  groupLabel: string;
  width: string;
  height: string;
  qty: string;
  priority: string;
  _priorityManual: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface SheetGroup {
  groupKey: string;
  groupLabel: string;
  parts: SheetPart2D[];
}

function buildSheetGroups(spec: ParsedSpec): SheetGroup[] {
  const groupMap = new Map<string, SheetGroup>();

  for (const row of spec.cuttingData) {
    if (row.unit !== 'м2') continue;
    if (!row.partLength || !row.partWidth) continue;

    const info = parseSheetMaterial(row.material);
    if (!info) continue;

    if (!groupMap.has(info.groupKey)) {
      groupMap.set(info.groupKey, { groupKey: info.groupKey, groupLabel: info.groupLabel, parts: [] });
    }

    const group = groupMap.get(info.groupKey)!;
    group.parts.push({
      partId: uid(),
      name: row.name || `${Math.round(row.partLength)}×${Math.round(row.partWidth)}`,
      w: row.partLength,
      h: row.partWidth,
      qty: row.totalQty,
      groupKey: info.groupKey,
    });
  }

  return Array.from(groupMap.values());
}

function makeDefaultStockRows(_groups: SheetGroup[], spec: ParsedSpec): SheetStockRow[] {
  const rows: SheetStockRow[] = [];
  const seenSizes = new Set<string>();

  for (const row of spec.cuttingData) {
    if (row.unit !== 'м2') continue;
    const info = parseSheetMaterial(row.material);
    if (!info) continue;

    const sizeKey = `${info.groupKey}|${info.sheetW}x${info.sheetH}`;
    if (seenSizes.has(sizeKey)) continue;
    seenSizes.add(sizeKey);

    rows.push({
      id: uid(),
      groupKey: info.groupKey,
      groupLabel: info.groupLabel,
      width: String(info.sheetW),
      height: String(info.sheetH),
      qty: '',
      priority: String(rows.filter((r) => r.groupKey === info.groupKey).length + 1),
      _priorityManual: false,
    });
  }

  return withAutoPriorities(rows);
}

function withAutoPriorities(rows: SheetStockRow[]): SheetStockRow[] {
  const groupCounters = new Map<string, number>();
  return rows.map((r) => {
    if (r._priorityManual) return r;
    const cur = (groupCounters.get(r.groupKey) ?? 0) + 1;
    groupCounters.set(r.groupKey, cur);
    return { ...r, priority: String(cur) };
  });
}

const NUM_INPUT =
  'w-full text-sm text-right border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400';

function thN(w: number, first = false): React.CSSProperties {
  return { paddingLeft: first ? 12 : 4, paddingRight: 4, paddingTop: 8, paddingBottom: 8, width: w, whiteSpace: 'nowrap' };
}
function tdN(first = false): React.CSSProperties {
  return { paddingLeft: first ? 12 : 4, paddingRight: 4, paddingTop: 6, paddingBottom: 6 };
}
const thMat: React.CSSProperties = { paddingLeft: 8, paddingRight: 8, paddingTop: 8, paddingBottom: 8 };
const tdMat: React.CSSProperties = { paddingLeft: 8, paddingRight: 8, paddingTop: 6, paddingBottom: 6 };

// ─── SheetCalcPanel ───────────────────────────────────────────────────────────

export function SheetCalcPanel({ spec, onResultsChange }: Props) {
  const groups = buildSheetGroups(spec);
  const worker = useCalculatorWorker();

  const [stockRows, setStockRows] = useState<SheetStockRow[]>(() => makeDefaultStockRows(groups, spec));
  const [kerf, setKerf] = useState('3');
  const [allowRotate, setAllowRotate] = useState(true);

  type CalcState =
    | { kind: 'idle' }
    | { kind: 'running' }
    | { kind: 'done'; results: SheetGroupResult[]; kerf: number }
    | { kind: 'error'; message: string };

  const [calcState, setCalcState] = useState<CalcState>({ kind: 'idle' });

  const hasAutoRun = useRef(false);
  useEffect(() => {
    if (groups.length === 0 || hasAutoRun.current) return;
    hasAutoRun.current = true;
    void handleCalculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (groups.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-400">
        Листовых материалов (ед. изм. «м2») в спецификации не найдено.
      </div>
    );
  }

  // ─── Row mutations ─────────────────────────────────────────────────────────

  function updateRow(id: string, patch: Partial<Omit<SheetStockRow, 'id'>>) {
    setStockRows((prev) => {
      const updated = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));
      if ('groupKey' in patch) {
        return withAutoPriorities(updated.map((r) => r.id === id ? { ...r, _priorityManual: false } : r));
      }
      return updated;
    });
  }

  function setPriority(id: string, value: string) {
    setStockRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, priority: value, _priorityManual: true } : r)),
    );
  }

  function addRow() {
    const group = groups[0];
    setStockRows((prev) =>
      withAutoPriorities([
        ...prev,
        {
          id: uid(),
          groupKey: group.groupKey,
          groupLabel: group.groupLabel,
          width: '',
          height: '',
          qty: '',
          priority: '',
          _priorityManual: false,
        },
      ]),
    );
  }

  function removeRow(id: string) {
    setStockRows((prev) => withAutoPriorities(prev.filter((r) => r.id !== id)));
  }

  // ─── Calculate ─────────────────────────────────────────────────────────────

  async function handleCalculate() {
    setCalcState({ kind: 'running' });
    try {
      const kerfNum = parseFloat(kerf) || 0;

      const validStocks: SheetStock2D[] = stockRows
        .filter((r) => parseFloat(r.width) > 0 && parseFloat(r.height) > 0)
        .map((r) => ({
          id: r.id,
          w: parseFloat(r.width),
          h: parseFloat(r.height),
          qty: r.qty === '' ? 9999 : parseInt(r.qty, 10) || 9999,
          groupKey: r.groupKey,
          label: r.groupLabel,
          priority: r.priority === '' ? 1 : parseInt(r.priority, 10) || 1,
        }));

      const results: SheetGroupResult[] = [];

      for (const group of groups) {
        const groupStocks = validStocks.filter((s) => s.groupKey === group.groupKey);
        if (groupStocks.length === 0 || group.parts.length === 0) continue;

        const result = await worker.calculate2d(groupStocks, group.parts, kerfNum, allowRotate);
        results.push({ groupKey: group.groupKey, groupLabel: group.groupLabel, result });
      }

      setCalcState({ kind: 'done', results, kerf: kerfNum });
      onResultsChange?.(results);
    } catch (e) {
      setCalcState({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <Collapsible title="Листы-заготовки" defaultOpen={true}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 28 }} />
              <col style={{ width: 84 }} />
              <col style={{ width: 84 }} />
              <col style={{ width: 68 }} />
              <col /* Марка — fills */ />
              <col style={{ width: 60 }} />
              <col style={{ width: 28 }} />
            </colgroup>
            <thead>
              <tr className="text-xs text-gray-400 border-b border-gray-100">
                <th style={thN(28, true)} className="font-medium text-left">#</th>
                <th style={thN(84)} className="font-medium text-right">Ширина, мм</th>
                <th style={thN(84)} className="font-medium text-right">Длина, мм</th>
                <th style={thN(68)} className="font-medium text-right">Кол-во</th>
                <th style={thMat} className="font-medium text-left">Марка</th>
                <th style={thN(60)} className="font-medium text-right">Приоритет</th>
                <th style={{ ...thN(28), paddingRight: 12 }} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {stockRows.map((row, i) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td style={tdN(true)} className="text-gray-400 text-xs">{i + 1}</td>
                  <td style={tdN()}>
                    <input type="number" min="1" value={row.width} placeholder="0"
                      onChange={(e) => updateRow(row.id, { width: e.target.value })}
                      className={NUM_INPUT} />
                  </td>
                  <td style={tdN()}>
                    <input type="number" min="1" value={row.height} placeholder="0"
                      onChange={(e) => updateRow(row.id, { height: e.target.value })}
                      className={NUM_INPUT} />
                  </td>
                  <td style={tdN()}>
                    <input type="number" min="1" step="1" value={row.qty} placeholder="∞"
                      onKeyDown={(e) => { if (e.key === '.' || e.key === ',') e.preventDefault(); }}
                      onChange={(e) => updateRow(row.id, { qty: e.target.value })}
                      className={NUM_INPUT} />
                  </td>
                  <td style={tdMat}>
                    <select
                      value={row.groupKey}
                      onChange={(e) => {
                        const grp = groups.find((g) => g.groupKey === e.target.value);
                        if (grp) updateRow(row.id, { groupKey: grp.groupKey, groupLabel: grp.groupLabel });
                      }}
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 truncate"
                    >
                      {groups.map((g) => (
                        <option key={g.groupKey} value={g.groupKey}>{g.groupLabel}</option>
                      ))}
                    </select>
                  </td>
                  <td style={tdN()}>
                    <input type="number" min="1" step="1" value={row.priority} placeholder="1"
                      onChange={(e) => setPriority(row.id, e.target.value)}
                      className={NUM_INPUT} />
                  </td>
                  <td style={{ ...tdN(), paddingRight: 12, textAlign: 'center' }}>
                    <button onClick={() => removeRow(row.id)}
                      className="text-gray-300 hover:text-red-400 transition-colors text-base leading-none"
                      title="Удалить">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-2 border-t border-gray-100">
          <button onClick={addRow} className="text-sm text-red-500 hover:text-red-600 font-medium">
            + Добавить лист
          </button>
        </div>

        {/* Kerf + rotation + calculate */}
        <div className="px-4 py-3 border-t border-gray-100 space-y-3">
          <div className="flex items-center gap-6 text-sm flex-wrap">
            <label className="flex items-center gap-1.5 text-gray-600">
              Зазор (рез), мм
              <input type="number" min="0" value={kerf}
                onChange={(e) => setKerf(e.target.value)}
                className="w-16 text-right border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            </label>
            <label className="flex items-center gap-2 text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={allowRotate}
                onChange={(e) => setAllowRotate(e.target.checked)}
                className="rounded" />
              Разрешить поворот деталей на 90°
            </label>
          </div>

          <button
            onClick={handleCalculate}
            disabled={calcState.kind === 'running'}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-sm font-medium py-2 rounded transition-colors"
          >
            {calcState.kind === 'running' ? 'Расчёт…' : 'Рассчитать листовой раскрой'}
          </button>

          {calcState.kind === 'error' && (
            <p className="text-xs text-red-600">{calcState.message}</p>
          )}
        </div>
      </Collapsible>

      {calcState.kind === 'done' && (
        <SheetCalcResultView groups={calcState.results} />
      )}
    </div>
  );
}
